/** @format */

import React, { useState, useEffect } from 'react';
import { 
  Car, 
  Camera, 
  Upload, 
  X, 
  Search, 
  Calendar,
  Clock,
  User,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@lib/supabaseClient';

// ✅ Composant ImageDropZone (identique à InterventionForm)
const ImageDropZone = ({ label, onImageUploaded, previewUrl }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(previewUrl || null);
  const [isCapturing, setIsCapturing] = useState(false);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.match('image.*')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      if (onImageUploaded) {
        onImageUploaded(file, e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('❌ Erreur caméra:', error);
      alert('Impossible d\'accéder à la caméra');
      setIsCapturing(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);

      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setPreview(imageData);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], `photo_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          if (onImageUploaded) {
            onImageUploaded(file, imageData);
          }
        },
        'image/jpeg',
        0.9
      );

      stopCamera();
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
  };

  useEffect(() => {
    if (previewUrl) {
      setPreview(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className='mb-4'>
      <p className='text-sm font-medium text-gray-300 mb-1'>{label}</p>

      {isCapturing ? (
        <div className='relative border-2 border-blue-500 rounded-lg overflow-hidden h-64'>
          <video
            ref={videoRef}
            className='w-full h-full object-cover'
            autoPlay
            playsInline
          />
          <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 flex justify-center gap-4'>
            <button
              onClick={capturePhoto}
              className='bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors'>
              <Camera className='w-5 h-5' />
              Capturer
            </button>
            <button
              onClick={stopCamera}
              className='bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors'>
              <X className='w-5 h-5' />
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer transition-colors h-48 ${
            isDragging
              ? 'border-blue-500 bg-blue-500/10'
              : 'border-slate-600 hover:border-blue-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() =>
            document.getElementById(`file-input-${label.replace(/\s+/g, '-')}`).click()
          }>
          {preview ? (
            <div className='relative w-full h-full'>
              <img
                src={preview}
                alt={label}
                className='w-full h-full object-contain'
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPreview(null);
                  if (onImageUploaded) {
                    onImageUploaded(null, null);
                  }
                }}
                className='absolute top-1 right-1 bg-red-500 rounded-full p-1 text-white hover:bg-red-600'>
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className='text-center'>
              <Upload className='h-8 w-8 text-slate-400 mb-2 mx-auto' />
              <p className='text-sm text-center text-slate-400 mb-3'>
                Déposez une image ici ou cliquez pour parcourir
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startCamera();
                }}
                className='bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors'>
                <Camera className='w-4 h-4' />
                Ouvrir la caméra
              </button>
            </div>
          )}
          <input
            type='file'
            id={`file-input-${label.replace(/\s+/g, '-')}`}
            className='hidden'
            accept='image/*'
            onChange={handleFileInput}
          />
        </div>
      )}
    </div>
  );
};

// ✅ Composant ReceptionForm
const ReceptionForm = ({ 
  onReceptionSaved, 
  initialData = null,
  onCancel 
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    immatriculation: initialData?.immatriculation || '',
    clientId: initialData?.client_id || null,
    clientName: initialData?.client_name || '',
    dateReception: initialData?.date_reception || new Date().toISOString().split('T')[0],
    heureReception: initialData?.heure_reception || '08:00',
    photos: {
      frontBefore: initialData?.photos?.frontBefore || null,
      rightBefore: initialData?.photos?.rightBefore || null,
      leftBefore: initialData?.photos?.leftBefore || null,
      backBefore: initialData?.photos?.backBefore || null,
    },
    notes: initialData?.notes || ''
  });

  // Recherche client
  const searchClient = async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, immatriculation, first_name, last_name')
        .or(`immatriculation.ilike.%${term}%,last_name.ilike.%${term}%,first_name.ilike.%${term}%`)
        .limit(10);

      if (error) throw error;

      setSearchResults(data || []);
      setShowResults(true);
    } catch (error) {
      console.error('❌ Erreur recherche client:', error);
    }
  };

  // Sélectionner un client
  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      immatriculation: client.immatriculation,
      clientId: client.id,
      clientName: `${client.last_name} ${client.first_name}`
    }));
    setSearchTerm('');
    setShowResults(false);
    setErrors(prev => ({ ...prev, immatriculation: null, clientName: null }));
  };

  // Upload photos dans dossier organisé
  const uploadPhotoToFolder = async (photoType, photoData) => {
    if (!photoData) return null;

    try {
      const response = await fetch(photoData);
      const blob = await response.blob();

      // Structure : reception/YYYY-MM-DD/IMMATRICULATION/type.jpg
      const folder = `reception/${formData.dateReception}/${formData.immatriculation}`;
      const fileName = `${folder}/${photoType}_${Date.now()}.jpg`;

      const { data, error } = await supabase.storage
        .from('vehicle-photos')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (error) {
        console.error(`❌ Erreur upload photo ${photoType}:`, error);
        return null;
      }

      console.log(`✅ Photo ${photoType} uploadée: ${fileName}`);
      return fileName;
    } catch (err) {
      console.error(`❌ Exception upload photo ${photoType}:`, err);
      return null;
    }
  };

  // Validation formulaire
  const validate = () => {
    const newErrors = {};

    if (!formData.immatriculation.trim()) {
      newErrors.immatriculation = "L'immatriculation est requise";
    }
    if (!formData.clientName.trim()) {
      newErrors.clientName = "Le nom du client est requis";
    }
    if (!formData.dateReception) {
      newErrors.dateReception = "La date de réception est requise";
    }
    if (!formData.photos.frontBefore) {
      newErrors.frontBefore = "La photo avant est requise";
    }
    if (!formData.photos.backBefore) {
      newErrors.backBefore = "La photo arrière est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sauvegarde réception
  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      console.log('📦 Upload photos...');

      // Upload toutes les photos
      const uploadedPhotos = {};
      for (const [type, data] of Object.entries(formData.photos)) {
        if (data) {
          const fileName = await uploadPhotoToFolder(type, data);
          if (fileName) {
            uploadedPhotos[type] = fileName;
          }
        }
      }

      console.log('💾 Sauvegarde réception...');

      const receptionData = {
        immatriculation: formData.immatriculation,
        client_id: formData.clientId,
        client_name: formData.clientName,
        date_reception: formData.dateReception,
        heure_reception: formData.heureReception,
        photos: uploadedPhotos,
        notes: formData.notes || null,
      };

      const { data: reception, error } = await supabase
        .from('receptions')
        .insert([receptionData])
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Réception enregistrée:', reception);

      setSuccess(true);
      setTimeout(() => {
        if (onReceptionSaved) {
          onReceptionSaved(reception);
        }
      }, 1500);

    } catch (error) {
      console.error('❌ Erreur sauvegarde réception:', error);
      setErrors({ submit: 'Erreur lors de l\'enregistrement' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='bg-slate-800/90 backdrop-blur-lg rounded-xl shadow-xl p-6 md:p-8'>
      {/* En-tête */}
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-3'>
          <Car className='w-8 h-8 text-blue-400' />
          <div>
            <h2 className='text-2xl font-bold text-blue-400'>
              Réception Véhicule
            </h2>
            <p className='text-sm text-gray-400'>
              Enregistrez la réception et les photos du véhicule
            </p>
          </div>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className='text-gray-400 hover:text-white'>
            <X className='w-6 h-6' />
          </button>
        )}
      </div>

      <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>

      {/* Recherche Client */}
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-300 mb-2'>
          Rechercher un véhicule / client
        </label>
        <div className='relative'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchClient(e.target.value);
              }}
              onFocus={() => searchTerm && setShowResults(true)}
              placeholder='Immatriculation ou nom du client...'
              className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
            />
          </div>

          {/* Résultats recherche */}
          {showResults && searchResults.length > 0 && (
            <>
              <div
                className='fixed inset-0 z-10'
                onClick={() => setShowResults(false)}
              />
              <div className='absolute z-20 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto'>
                {searchResults.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => selectClient(client)}
                    className='w-full text-left px-4 py-3 hover:bg-slate-600 text-white transition-colors border-b border-slate-600 last:border-0'>
                    <div className='font-medium'>
                      {client.immatriculation}
                    </div>
                    <div className='text-sm text-gray-400'>
                      {client.last_name} {client.first_name}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Infos véhicule/client */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Immatriculation *
          </label>
          <div className='relative'>
            <Car className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              value={formData.immatriculation}
              onChange={(e) => setFormData(prev => ({ ...prev, immatriculation: e.target.value.toUpperCase() }))}
              placeholder='AA-123-BB'
              className={`w-full bg-slate-700 border ${errors.immatriculation ? 'border-red-500' : 'border-slate-600'} rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500`}
            />
          </div>
          {errors.immatriculation && (
            <p className='mt-1 text-sm text-red-400'>{errors.immatriculation}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Client *
          </label>
          <div className='relative'>
            <User className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              value={formData.clientName}
              onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
              placeholder='Nom du client'
              className={`w-full bg-slate-700 border ${errors.clientName ? 'border-red-500' : 'border-slate-600'} rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500`}
            />
          </div>
          {errors.clientName && (
            <p className='mt-1 text-sm text-red-400'>{errors.clientName}</p>
          )}
        </div>
      </div>

      {/* Date/heure réception */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Date de réception *
          </label>
          <div className='relative'>
            <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='date'
              value={formData.dateReception}
              onChange={(e) => setFormData(prev => ({ ...prev, dateReception: e.target.value }))}
              className={`w-full bg-slate-700 border ${errors.dateReception ? 'border-red-500' : 'border-slate-600'} rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-blue-500`}
            />
          </div>
          {errors.dateReception && (
            <p className='mt-1 text-sm text-red-400'>{errors.dateReception}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Heure de réception
          </label>
          <div className='relative'>
            <Clock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='time'
              value={formData.heureReception}
              onChange={(e) => setFormData(prev => ({ ...prev, heureReception: e.target.value }))}
              className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-blue-500'
            />
          </div>
        </div>
      </div>

      {/* Photos */}
      <div className='mb-6'>
        <div className='flex items-center gap-2 mb-4'>
          <Camera className='w-5 h-5 text-blue-400' />
          <h3 className='text-lg font-semibold text-blue-400'>
            Photos du véhicule
          </h3>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          <div>
            <ImageDropZone
              label='Vue avant *'
              onImageUploaded={(file, preview) =>
                setFormData(prev => ({
                  ...prev,
                  photos: { ...prev.photos, frontBefore: preview }
                }))
              }
              previewUrl={formData.photos.frontBefore}
            />
            {errors.frontBefore && (
              <p className='mt-1 text-sm text-red-400'>{errors.frontBefore}</p>
            )}
          </div>

          <div>
            <ImageDropZone
              label='Vue droite'
              onImageUploaded={(file, preview) =>
                setFormData(prev => ({
                  ...prev,
                  photos: { ...prev.photos, rightBefore: preview }
                }))
              }
              previewUrl={formData.photos.rightBefore}
            />
          </div>

          <div>
            <ImageDropZone
              label='Vue gauche'
              onImageUploaded={(file, preview) =>
                setFormData(prev => ({
                  ...prev,
                  photos: { ...prev.photos, leftBefore: preview }
                }))
              }
              previewUrl={formData.photos.leftBefore}
            />
          </div>

          <div>
            <ImageDropZone
              label='Vue arrière *'
              onImageUploaded={(file, preview) =>
                setFormData(prev => ({
                  ...prev,
                  photos: { ...prev.photos, backBefore: preview }
                }))
              }
              previewUrl={formData.photos.backBefore}
            />
            {errors.backBefore && (
              <p className='mt-1 text-sm text-red-400'>{errors.backBefore}</p>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-300 mb-2'>
          Notes / Observations
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder='État général du véhicule, dommages visibles...'
          rows={3}
          className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
        />
      </div>

      {/* Messages erreur/succès */}
      {errors.submit && (
        <div className='mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-3'>
          <AlertCircle className='w-5 h-5 text-red-400' />
          <p className='text-red-400'>{errors.submit}</p>
        </div>
      )}

      {success && (
        <div className='mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3'>
          <CheckCircle className='w-5 h-5 text-green-400' />
          <p className='text-green-400'>Réception enregistrée avec succès !</p>
        </div>
      )}

      {/* Boutons */}
      <div className='flex justify-end gap-4'>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={loading}
            className='px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
            Annuler
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={loading || success}
          className='px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'>
          {loading ? (
            <>
              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
              <span>Enregistrement...</span>
            </>
          ) : success ? (
            <>
              <CheckCircle className='w-5 h-5' />
              <span>Enregistré !</span>
            </>
          ) : (
            <>
              <CheckCircle className='w-5 h-5' />
              <span>Enregistrer la réception</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ReceptionForm;